// OOXML-based types for Word track changes integration
export type OoxmlChange = string; // OOXML string with track changes markup

// Legacy Delta type - kept for backward compatibility during migration
export type Delta = [number, string, number]; // [operation, text, position] - DEPRECATED

/**
 * ============================================================================
 * OOXML UTILITIES FOR WORD TRACK CHANGES
 * ============================================================================
 */

/**
 * Validates if a string contains valid OOXML markup
 */
export function isValidOoxml(ooxmlString: string): boolean {
  if (!ooxmlString || typeof ooxmlString !== 'string') {
    return false;
  }
  
  // Basic validation - check for Word namespace tags
  const hasWordNamespace = ooxmlString.includes('w:') || ooxmlString.includes('<w:');
  const hasClosingTags = ooxmlString.includes('</');
  
  return hasWordNamespace && hasClosingTags;
}

/**
 * Extracts track changes information from OOXML
 */
export function extractTrackChangesInfo(ooxmlString: string): {
  hasInsertions: boolean;
  hasDeletions: boolean;
  authors: string[];
} {
  const hasInsertions = ooxmlString.includes('<w:ins') || ooxmlString.includes('<w:ins>');
  const hasDeletions = ooxmlString.includes('<w:del') || ooxmlString.includes('<w:del>');
  
  // Extract author names
  const authorMatches = ooxmlString.matchAll(/w:author=['"]([^'"]+)['"]/g);
  const authors = Array.from(new Set(Array.from(authorMatches, m => m[1])));
  
  return {
    hasInsertions,
    hasDeletions,
    authors
  };
}

/**
 * Individual change extracted from OOXML
 */
export interface OoxmlChangeDetail {
  id: string;
  ooxmlId: string;  // ID del tag OOXML <w:ins w:id="3">
  type: 'insertion' | 'deletion';
  text: string;
  author: string;
  date: string;
  position: number; // Approximate position in document
}

/**
 * Tracked change extraído directamente del OOXML con su ID original
 * Se usa para mapeo robusto contra TrackedChanges
 */
export interface OoxmlTrackedChange {
  ooxmlId: string;  // ID del tag <w:ins w:id="3">
  type: 'insertion' | 'deletion';
  text: string;
  author: string;
  date: string;
  position: number;
}

/**
 * Grouped change (can be replacement, insertion only, deletion only, or table)
 */
export interface OoxmlChangeGroup {
  id: string;
  type: 'replacement' | 'insertion' | 'deletion' | 'table-insertion' | 'table-deletion';
  deleted?: OoxmlChangeDetail;
  inserted?: OoxmlChangeDetail;
  
  // Table-specific information
  tableInfo?: {
    rows: number;
    columns: number;
    headers?: string[];
    summary: string;
    // Full table data for rendering
    data?: string[][]; // Array of rows, each row is array of cells
  };
}

/**
 * Extracts text content from OOXML tags
 * Handles both <w:t> (for insertions) and <w:delText> (for deletions)
 */
function extractTextFromOoxmlFragment(fragment: string): string {
  // Match both <w:t> and <w:delText> tags
  const textMatches = fragment.matchAll(/<w:(?:t|delText)[^>]*>([^<]*)<\/w:(?:t|delText)>/g);
  return Array.from(textMatches, m => m[1]).join('');
}

/**
 * Checks if an OOXML fragment contains a table
 */
function containsTable(fragment: string): boolean {
  return fragment.includes('<w:tbl>') || fragment.includes('<w:tbl ');
}

/**
 * Extracts table information from OOXML fragment
 */
function extractTableInfo(tableFragment: string): {
  rows: number;
  columns: number;
  headers?: string[];
  summary: string;
  data?: string[][];
} {
  // Extract all rows
  const rowMatches = tableFragment.match(/<w:tr[\s\S]*?<\/w:tr>/g) || [];
  const rows = rowMatches.length;
  
  // Count columns from first row
  let columns = 0;
  if (rowMatches.length > 0) {
    const firstRow = rowMatches[0];
    const cellMatches = firstRow.match(/<w:tc[\s\S]*?<\/w:tc>/g) || [];
    columns = cellMatches.length;
  }
  
  // Extract ALL table data (all rows and cells)
  const data: string[][] = [];
  for (let i = 0; i < rowMatches.length; i++) {
    const row = rowMatches[i];
    const cellMatches = row.match(/<w:tc[\s\S]*?<\/w:tc>/g) || [];
    const rowData: string[] = [];
    
    for (let j = 0; j < cellMatches.length; j++) {
      const cell = cellMatches[j];
      const text = extractTextFromOoxmlFragment(cell);
      rowData.push(text.trim() || '');
    }
    
    data.push(rowData);
  }
  
  // Extract headers from first row (if exists)
  let headers: string[] | undefined;
  if (data.length > 0 && data[0].length > 0) {
    headers = data[0].filter(h => h.length > 0);
    if (headers.length === 0) {
      headers = undefined;
    }
  }
  
  // Create summary
  const headerSummary = headers && headers.length > 0 
    ? `: ${headers.join(' | ')}`
    : '';
  const summary = `Tabla ${rows}×${columns}${headerSummary}`;
  
  return {
    rows,
    columns,
    headers,
    summary,
    data
  };
}

/**
 * Parses all changes from OOXML markup
 */
export function parseOoxmlChanges(ooxmlString: string): OoxmlChangeDetail[] {
  const changes: OoxmlChangeDetail[] = [];
  
  // INSERTIONS: <w:ins w:id="..." w:author="..." w:date="...">...</w:ins>
  const insertRegex = /<w:ins\s+w:id="(\d+)"\s+w:author="([^"]+)"\s+w:date="([^"]+)"[^>]*>([\s\S]*?)<\/w:ins>/g;
  let insertMatches = Array.from(ooxmlString.matchAll(insertRegex));
  
  for (let i = 0; i < insertMatches.length; i++) {
    const match = insertMatches[i];
    const text = extractTextFromOoxmlFragment(match[4]);
    
    if (text.trim()) { // Solo agregar si tiene texto
      changes.push({
        id: `ins-${match[1]}-${i}`,
        ooxmlId: match[1],  // ID original del OOXML
        type: 'insertion',
        text: text,
        author: match[2],
        date: match[3],
        position: match.index || 0
      });
    }
  }
  
  // DELETIONS: <w:del w:id="..." w:author="..." w:date="...">...</w:del>
  const deleteRegex = /<w:del\s+w:id="(\d+)"\s+w:author="([^"]+)"\s+w:date="([^"]+)"[^>]*>([\s\S]*?)<\/w:del>/g;
  let deleteMatches = Array.from(ooxmlString.matchAll(deleteRegex));
  
  for (let i = 0; i < deleteMatches.length; i++) {
    const match = deleteMatches[i];
    const text = extractTextFromOoxmlFragment(match[4]);
    
    if (text.trim()) { // Solo agregar si tiene texto
      changes.push({
        id: `del-${match[1]}-${i}`,
        ooxmlId: match[1],  // ID original del OOXML
        type: 'deletion',
        text: text,
        author: match[2],
        date: match[3],
        position: match.index || 0
      });
    }
  }
  
  // Ordenar por posición en el documento
  changes.sort((a, b) => a.position - b.position);
  
  return changes;
}

/**
 * Groups changes into pairs (replacement, insertion, deletion, tables)
 * Automatically pairs deletions followed by insertions as "replacements"
 * Detects and groups table insertions/deletions as single changes
 */
export function groupOoxmlChanges(ooxmlString: string): OoxmlChangeGroup[] {
  const groups: OoxmlChangeGroup[] = [];
  
  // STEP 1: Detect and group table insertions/deletions
  const tableInsertRegex = /<w:tbl>([\s\S]*?)<\/w:tbl>/g;
  const processedChangeIds = new Set<string>();
  
  // Convert iterator to array for ES5 compatibility
  const tableMatches = Array.from(ooxmlString.matchAll(tableInsertRegex));
  
  for (let j = 0; j < tableMatches.length; j++) {
    const tableMatch = tableMatches[j];
    const tableContent = tableMatch[0];
    
    // Check if table has insertion or deletion marks
    const hasInsertion = tableContent.includes('<w:ins ');
    const hasDeletion = tableContent.includes('<w:del ');
    
    if (hasInsertion || hasDeletion) {
      const tableInfo = extractTableInfo(tableContent);
      
      groups.push({
        id: `table-${groups.length}`,
        type: hasInsertion ? 'table-insertion' : 'table-deletion',
        tableInfo
      });
      
      // Extract ALL change IDs from within this table to exclude them later
      // Match both <w:ins> and <w:del> tags with their IDs
      const insRegex = /<w:ins\s+w:id="(\d+)"/g;
      const delRegex = /<w:del\s+w:id="(\d+)"/g;
      
      // Extract insertion IDs - store all possible ID formats
      const insMatches = Array.from(tableContent.matchAll(insRegex));
      for (let m = 0; m < insMatches.length; m++) {
        const rawId = insMatches[m][1];
        // Add multiple formats to cover all cases
        processedChangeIds.add(rawId);
        // The parseOoxmlChanges function creates IDs like "ins-{id}-{index}"
        // We'll mark the raw ID and check it when filtering
      }
      
      // Extract deletion IDs
      const delMatches = Array.from(tableContent.matchAll(delRegex));
      for (let m = 0; m < delMatches.length; m++) {
        const rawId = delMatches[m][1];
        processedChangeIds.add(rawId);
      }
    }
  }
  
  // STEP 2: Parse regular changes (non-table)
  const changes = parseOoxmlChanges(ooxmlString);
  
  // Filter out changes that are inside tables we already processed (by ID)
  const nonTableChanges = changes.filter(change => {
    // Extract the raw OOXML ID from our change ID format
    // Format is "ins-{rawId}-{index}" or "del-{rawId}-{index}"
    const idMatch = change.id.match(/^(ins|del)-(\d+)-\d+$/);
    if (idMatch) {
      const rawId = idMatch[2];
      return !processedChangeIds.has(rawId);
    }
    return true; // Keep changes that don't match the expected format
  });
  
  // STEP 3: Group regular changes as before
  let i = 0;
  while (i < nonTableChanges.length) {
    const current = nonTableChanges[i];
    const next = nonTableChanges[i + 1];
    
    // Si hay una eliminación seguida de una inserción (dentro de 1000 caracteres)
    // Las consideramos un "reemplazo"
    if (
      current.type === 'deletion' && 
      next && 
      next.type === 'insertion' &&
      (next.position - current.position) < 1000
    ) {
      groups.push({
        id: `group-${groups.length}`,
        type: 'replacement',
        deleted: current,
        inserted: next
      });
      i += 2; // Saltar ambos cambios
      continue;
    }
    
    // Cambio individual (solo inserción o solo eliminación)
    if (current.type === 'insertion') {
      groups.push({
        id: `group-${groups.length}`,
        type: 'insertion',
        inserted: current
      });
    } else {
      groups.push({
        id: `group-${groups.length}`,
        type: 'deletion',
        deleted: current
      });
    }
    
    i++;
  }
  
  return groups;
}

/**
 * ============================================================================
 * LEGACY DELTA FUNCTIONS - DEPRECATED
 * These are kept for backward compatibility during migration
 * Will be removed in future versions
 * ============================================================================
 */

// Note: HTML-based delta functions have been removed
// The add-in now uses native OOXML with Word track changes 

/**
 * Parsea TODOS los cambios del OOXML (incluyendo vacíos)
 * Mantiene el orden secuencial para mapeo exacto con TrackedChanges
 * CRÍTICO: No filtrar cambios sin texto para mantener sincronización
 */
export function parseOoxmlTrackedChangesWithIds(ooxmlString: string): OoxmlTrackedChange[] {
  const changes: OoxmlTrackedChange[] = [];
  
  // INSERTIONS: <w:ins w:id="..." w:author="..." w:date="...">...</w:ins>
  const insertRegex = /<w:ins\s+w:id="(\d+)"\s+w:author="([^"]+)"\s+w:date="([^"]+)"[^>]*>([\s\S]*?)<\/w:ins>/g;
  const insertMatches = Array.from(ooxmlString.matchAll(insertRegex));
  
  for (let i = 0; i < insertMatches.length; i++) {
    const match = insertMatches[i];
    const text = extractTextFromOoxmlFragment(match[4]);
    
    // 🆕 NO FILTRAR - incluir TODOS los cambios (incluso vacíos)
    changes.push({
      ooxmlId: match[1],      // ID original del OOXML
      type: 'insertion',
      text: text.trim(),      // Puede estar vacío
      author: match[2],
      date: match[3],
      position: match.index || 0
    });
  }
  
  // DELETIONS: <w:del w:id="..." w:author="..." w:date="...">...</w:del>
  const deleteRegex = /<w:del\s+w:id="(\d+)"\s+w:author="([^"]+)"\s+w:date="([^"]+)"[^>]*>([\s\S]*?)<\/w:del>/g;
  const deleteMatches = Array.from(ooxmlString.matchAll(deleteRegex));
  
  for (let i = 0; i < deleteMatches.length; i++) {
    const match = deleteMatches[i];
    const text = extractTextFromOoxmlFragment(match[4]);
    
    // 🆕 NO FILTRAR - incluir TODOS los cambios (incluso vacíos)
    changes.push({
      ooxmlId: match[1],
      type: 'deletion',
      text: text.trim(),      // Puede estar vacío
      author: match[2],
      date: match[3],
      position: match.index || 0
    });
  }
  
  // Ordenar por posición en el documento
  changes.sort((a, b) => a.position - b.position);
  
  return changes;
}


